import React, { useMemo, useState } from 'react'
import { Send } from 'lucide-react'
import { PartnerService, UserService } from '@duck/api-definitions'
import { Button, Checkbox, Input, Skeleton } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { bestGradeForPerson } from '../cohortV2Data'
import { PersonRow, type PersonRowModel } from './PersonRow'

interface PeopleTabProps {
  persons: UserService.Person[]
  assignmentsDetails: PartnerService.AssignmentDetails[]
  isLoading: boolean
  checkedPersonIds: Set<string>
  setCheckedPersonIds: React.Dispatch<React.SetStateAction<Set<string>>>
  onSendToSelected: () => void
}

export const PeopleTab: React.FC<PeopleTabProps> = ({
  persons, assignmentsDetails, isLoading, checkedPersonIds, setCheckedPersonIds, onSendToSelected,
}) => {
  const { t } = useTar()
  const [search, setSearch] = useState('')

  const models = useMemo<PersonRowModel[]>(() => {
    return persons.map((person) => {
      const personDetails = assignmentsDetails.filter((d) => d.personId === person.id)
      return {
        person,
        totalAssignments: personDetails.length,
        submittedAssignments: personDetails.filter((d) => d.status === PartnerService.AssignmentDetailsStatus.Submitted).length,
        bestGrade: bestGradeForPerson(person.id, personDetails),
      }
    })
  }, [persons, assignmentsDetails])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return models
    return models.filter((m) => {
      const name = `${m.person.firstName || ''} ${m.person.lastName || ''}`.toLowerCase()
      return name.includes(q) || (m.person.email || '').toLowerCase().includes(q)
    })
  }, [models, search])

  const filteredIds = useMemo(() => filtered.map((m) => m.person.id), [filtered])
  const allChecked = filteredIds.length > 0 && filteredIds.every((id) => checkedPersonIds.has(id))
  const someChecked = filteredIds.some((id) => checkedPersonIds.has(id))
  const toggleAll = () =>
    setCheckedPersonIds((prev) => {
      const next = new Set(prev)
      if (allChecked) filteredIds.forEach((id) => next.delete(id))
      else filteredIds.forEach((id) => next.add(id))
      return next
    })

  const toggle = (id: string, checked: boolean) =>
    setCheckedPersonIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id); else next.delete(id)
      return next
    })

  if (isLoading) {
    return <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-[60px] w-full rounded-lg" />)}</div>
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Input placeholder={t('partner.searchPeople')} value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
      </div>

      {checkedPersonIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm text-primary">
          <span>{t('partner.nSelected', { count: checkedPersonIds.size })}</span>
          <Button size="sm" className="ms-auto gap-2" onClick={onSendToSelected}>
            <Send className="h-4 w-4" />{t('partner.sendAssignment')}
          </Button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="grid h-9 grid-cols-[26px_1fr_auto] items-center gap-3 border-b border-border bg-muted/20 px-3.5 text-[10px] uppercase tracking-wider text-muted-foreground lg:grid-cols-[26px_1.5fr_1.4fr_140px_1fr]">
          <Checkbox
            aria-label={t('partner.selectAll')}
            checked={allChecked ? true : someChecked ? 'indeterminate' : false}
            onCheckedChange={toggleAll}
            disabled={filteredIds.length === 0}
            className="h-4 w-4"
          />
          <span>{t('common.name')}</span>
          <span className="hidden lg:block">{t('common.email')}</span>
          <span className="hidden lg:block">{t('partner.status')}</span>
          <span>{t('partner.assignments')}</span>
        </div>
        {filtered.map((m) => (
          <PersonRow key={m.person.id} model={m} checked={checkedPersonIds.has(m.person.id)} onToggle={(c) => toggle(m.person.id, c)} />
        ))}
        {filtered.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">{t('common.empty')}</p>}
      </div>
    </div>
  )
}
